import { x } from '@xstyled/styled-components';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { TEXT } from '../itemIds';
import {
  getIcon,
  IconVariants
} from '~/training/sequence/templates/items/text/Icons';
import { useTrainingStore } from '~/training/state';
import audioEffect from '~/assets/mp3/keyboard-typing.mp3';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import styled, { css } from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { Responsive, useResponsive } from '~/lib/useResponsive';

import { handlePaste } from '../helper/eventForContentEditable';
import { setActivePortal } from './GlobalRewritePortal';
import {
  getDecodedText,
  getRawContentLength,
  sanitizeRawHtmlContent
} from './helper/textInputValidation';
import { stripHtmlTags } from '~/lib/sanitization';

let activePopupId: string | null = null;

// Quiz dropdown marker - replace XXX with draggable element
const QUIZ_DROPDOWN_MARKER = 'XXX';
// Zero-width spaces around the dropdown help the browser handle cursor positioning correctly
const ZERO_WIDTH_SPACE = '\u200B';

const getQuizDropdownHTML = (draggable: boolean): string => {
  const cursor = draggable ? 'move' : 'default';
  return `${ZERO_WIDTH_SPACE}<span data-quiz-dropdown="true" draggable="${draggable}" contenteditable="false" style="display: inline-block; background: #FFF; color: #000; padding: 2px 12px; border-radius: 10px; cursor: ${cursor}; user-select: none; font-family: ralewayBold; margin: 0 2px; border-color: #00A88A; box-shadow: 0 4px 0 #00A88A; border: 2px solid #00A88A;">Dropdown</span>${ZERO_WIDTH_SPACE}`;
};

export const QUIZ_DROPDOWN_HTML = getQuizDropdownHTML(true);

// Convert XXX markers to dropdown elements for display
const convertMarkersToDropdowns = (html: string, draggable = true): string => {
  return html.replace(
    new RegExp(QUIZ_DROPDOWN_MARKER, 'g'),
    getQuizDropdownHTML(draggable)
  );
};

// Convert dropdown elements back to XXX markers for saving
export const convertDropdownsToMarkers = (html: string): string => {
  // Replace the dropdown span (with surrounding zero-width spaces) with XXX
  let result = html.replace(
    /\u200B?<span[^>]*data-quiz-dropdown="true"[^>]*>.*?<\/span>\u200B?/gi,
    QUIZ_DROPDOWN_MARKER
  );

  // Clean up any remaining zero-width spaces
  result = result.replace(/\u200B/g, '');

  // If XXX marker is missing (user somehow deleted the dropdown), add it at the end
  if (!result.includes(QUIZ_DROPDOWN_MARKER)) {
    result = result + ' ' + QUIZ_DROPDOWN_MARKER;
  }

  return result;
};

const calculatePopupPosition = (
  rect: DOMRect,
  popupWidth = 370,
  popupHeight = 260
) => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  let x = rect.x - 50;
  let y = rect.y + 25;

  if (x + popupWidth > screenWidth) {
    x = screenWidth - popupWidth - 10;
  }

  if (x < 10) {
    x = 10;
  }

  if (y + popupHeight > screenHeight) {
    y = rect.y - popupHeight - 10;
  }

  if (y < 10) {
    y = 10;
  }

  return { x, y };
};

/** Get HTML truncated to first N visible characters (HTML tags don't count, aren't shown as text) */
function getHtmlAtVisibleLength(html: string, visibleLength: number): string {
  const segments = html.split(/(<[^>]+>)/g);
  let result = '';
  let count = 0;
  for (const seg of segments) {
    if (seg.startsWith('<')) {
      result += seg;
    } else {
      const toShow = Math.min(seg.length, Math.max(0, visibleLength - count));
      result += seg.slice(0, toShow);
      count += seg.length;
      if (count >= visibleLength) break;
    }
  }
  return result;
}

function getVisibleTextLength(html: string): number {
  return stripHtmlTags(html).length;
}

const TypingCursor = styled.span`
  &::after {
    content: '|';
    animation: typingCursor 1.1s infinite step-start;
  }
  @keyframes typingCursor {
    50% {
      opacity: 0;
    }
  }
`;

const TYPING_SPEED_MS = 40;

const HtmlTypeAnimation = ({
  html,
  onComplete,
  cursor = true
}: {
  html: string;
  onComplete?: () => void;
  cursor?: boolean;
}) => {
  const normalizedHtml =
    html?.replace(/&#39;/g, "'").replace(/&quot;/g, '"') ?? '';
  const totalLength = getVisibleTextLength(normalizedHtml);
  const [visibleLength, setVisibleLength] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (totalLength === 0) {
      onCompleteRef.current?.();
      return;
    }
    let charsShown = 0;
    const interval = setInterval(() => {
      charsShown += 1;
      setVisibleLength(charsShown);
      if (charsShown >= totalLength) {
        clearInterval(interval);
        onCompleteRef.current?.();
      }
    }, TYPING_SPEED_MS);
    return () => clearInterval(interval);
  }, [normalizedHtml, totalLength]);

  const displayHtml = getHtmlAtVisibleLength(normalizedHtml, visibleLength);
  const isTypingComplete = visibleLength >= totalLength;

  return (
    <>
      <span dangerouslySetInnerHTML={{ __html: displayHtml }} />
      {cursor && !isTypingComplete && <TypingCursor />}
    </>
  );
};

const EditableStyle = css`
  &[contenteditable='true'] {
    position: relative;
    border: 2px solid transparent;
    border-radius: 8px;

    &:focus {
      border: 2px solid var(--color-accent-dk);
    }

    &:focus:after {
      opacity: 0;
    }

    &:hover {
      border: 2px solid #9738ff;
    }
  }
`;

const EditableH1 = styled(x.h1)`
  word-break: break-word;

  ${EditableStyle}
  b {
    font-family: recoletaBold !important;
  }
  a {
    text-decoration: underline !important;
  }
`;

const EditableH2 = styled(x.h2)<{ quotes?: boolean }>`
  word-break: break-word;

  ${EditableStyle}
  b {
    font-family: recoletaBold !important;
  }
  a {
    text-decoration: underline !important;
  }
  ${p =>
    p.quotes
      ? css`
          &:before {
            content: '“';
            font-size: 80px;
            line-height: 80px;
            color: ${p.theme.colors.accentDark};
            position: absolute;
            display: block;

            margin-left: -38px;
            margin-top: 0px;
          }
        `
      : ''}
`;

const EditableH3 = styled(x.h3)`
  word-break: break-word;

  ${EditableStyle}
  b {
    font-family: recoletaBold !important;
  }
  a {
    text-decoration: underline !important;
  }
`;

const EditableH4 = styled(x.h4)`
  word-break: break-word;

  ${EditableStyle}
  b {
    font-family: recoletaBold !important;
  }
  a {
    text-decoration: underline !important;
  }
`;

const EditableH5 = styled(x.h5)`
  word-break: break-word;

  ${EditableStyle}
  b {
    font-family: recoletaBold !important;
  }
  a {
    text-decoration: underline !important;
  }
`;

const EditableSpan = styled(x.span)`
  word-break: break-word;

  ${EditableStyle}
  b {
    font-family: ralewayBold !important;
  }
  a {
    text-decoration: underline !important;
  }
`;

const EditableDiv = styled(x.div)`
  word-break: break-word;

  ${EditableStyle}
  b {
    font-family: ralewayBold !important;
  }
  a {
    text-decoration: underline !important;
  }

  &.cards3-body {
    p:first-of-type {
      padding-bottom: 24px;
    }
  }
`;

/** Slide body copy — EDS Body/L/Regular (Plus Jakarta Sans 16/400/24). */
const EditableBodyText = styled(EditableDiv)`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;

  b,
  strong {
    font-family: 'Plus Jakarta Sans', sans-serif !important;
    font-weight: 700;
  }
`;

const TextSection = styled.div`
  word-break: break-word;

  a {
    text-decoration: underline;
  }

  b {
    font-family: ralewayBold;
  }

  /* list-style-position:inside keeps markers on the text column (aligned
   * with paragraphs) and avoids Safari clipping outside markers under
   * overflow:hidden (SocialMedia2 Tweet). Reset UA padding so browsers match. */
  ol,
  ul {
    margin-left: 0;
    padding-left: 0;
    list-style-position: inside;
  }

  ol {
    list-style-type: decimal;
  }

  ul {
    list-style-type: disc;
  }
`;

/** Bottom fade when body text overflows — tracks --color-white so dark mode
 *  fades into the inverted surface instead of a bright white strip. */
const ScrollFadeOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 30px;
  pointer-events: none;
  border-radius: 0 0 8px 8px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    color-mix(in srgb, var(--color-white) 90%, transparent) 100%
  );
`;

export type TextVariants =
  | 'heading1'
  | 'heading1_bolder'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5'
  | 'sub_heading'
  | 'quote'
  | 'text'
  | 'huge'
  | 'titleForCard';

/** Variants that render an <h1>–<h5> element. */
const HEADING_VARIANTS: TextVariants[] = [
  'heading1',
  'heading1_bolder',
  'heading2',
  'heading3',
  'heading4',
  'heading5',
  'sub_heading',
  'titleForCard'
];

/**
 * True when the HTML value contains real text (not just empty tags,
 * `&nbsp;` or whitespace). Empty headings are an axe violation
 * (`empty-heading`), so such headings are skipped in learner mode.
 */
const hasVisibleText = (html?: string): boolean =>
  Boolean(
    html &&
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\u200B/g, '')
      .trim()
  );

// 🚫 Prevent image/file drops inside editable text boxes (but allow quiz dropdown drag)
const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
  // Allow quiz dropdown drag and drop
  if (e.dataTransfer?.getData('text/plain') === 'quiz-dropdown') {
    return; // Don't prevent - let the custom handler deal with it
  }
  e.preventDefault();
};

const variantTable: {
  [key in TextVariants]: FC<{
    id: string;
    value: string;
    contentEditable?: boolean;
    icon?: string;
    canScroll?: boolean;
    typing?: boolean;
    className?: string;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    onInput?: React.ChangeEventHandler<HTMLDivElement>;
    onPaste?: React.ClipboardEventHandler<HTMLDivElement>;
    onFocus?: React.FocusEventHandler<HTMLDivElement>;
    quotes?: boolean;
    padding?: Responsive<string>;
    fontSize?: string;
    maxHeight?: string;
    lineHeight?: string;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>; // allow key restriction
    center?: boolean; // center alignment for heading1 when requested
    textAlignment?: boolean;
    canUseMarkTag?: boolean;
  }>;
} = {
  heading1: ({
    id,
    value,
    contentEditable = false,
    typing = false,
    onBlur,
    onInput,
    onPaste,
    fontSize = '24px',
    padding,
    onFocus,
    lineHeight: lh,
    onKeyDown,
    center
  }) => {
    if (typing) {
      const { editorMode } = useEditorStore();
      const { addBlocker, removeBlocker } = useSequenceStore();
      const typingBlockerId = `text_typing_${id}`;
      /** Full text for AT: the typewriter starts empty (axe `empty-heading`). */
      const plainText = (value ?? '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const [playAudioEffect, stopAudioEffect] = useAudioEffect(
        audioEffect,
        true
      );

      useEffect(() => {
        try {
          playAudioEffect();
        } catch (error) {
          // Silently ignore audio effect errors
        }
        return () => {
          stopAudioEffect();
        };
      }, []);

      useEffect(() => {
        const shouldBlockNext =
          !contentEditable &&
          !editorMode &&
          getVisibleTextLength(value ?? '') > 0;

        if (shouldBlockNext) {
          addBlocker(typingBlockerId);
        }

        return () => {
          removeBlocker(typingBlockerId);
        };
      }, [
        contentEditable,
        editorMode,
        value,
        typingBlockerId,
        addBlocker,
        removeBlocker
      ]);

      const handleTypingComplete = () => {
        stopAudioEffect();
        removeBlocker(typingBlockerId);
      };

      return (
        <EditableH1
          onBlur={onBlur}
          zIndex={1}
          contentEditable={contentEditable}
          p={padding ?? { _: '20px 30px', md: '10px 10px 10px 0px' }}
          lineHeight={{ _: lh || '32px' }}
          fontSize={{ _: fontSize }}
          {...(center ? { textAlign: { _: 'center' } } : {})}
          suppressContentEditableWarning={true}
          onInput={onInput}
          onPaste={onPaste}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onDrop={handleDrop}
          data-id={id}
          aria-label={plainText || undefined}
        >
          <span aria-hidden={true}>
            <HtmlTypeAnimation
              html={value ?? ''}
              cursor={true}
              onComplete={handleTypingComplete}
            />
          </span>
        </EditableH1>
      );
    }

    const element = (
      <EditableH1
        onBlur={onBlur}
        zIndex={1}
        contentEditable={contentEditable}
        p={padding ?? { _: '20px 30px', md: '10px 10px 10px 0px' }}
        // marginTop={{ _: '10px' }}
        lineHeight={{ _: lh || '32px' }}
        fontSize={{ _: fontSize }}
        {...(center ? { textAlign: { _: 'center' } } : {})}
        dangerouslySetInnerHTML={{ __html: value }}
        suppressContentEditableWarning={true}
        onInput={onInput}
        onPaste={onPaste}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDrop={handleDrop}
        data-id={id}
      />
    );

    return contentEditable ? <TextSection>{element}</TextSection> : element;
  },
  heading1_bolder: ({
    id,
    value,
    contentEditable = false,
    onBlur,
    onInput,
    onPaste,
    onFocus
  }) => {
    return (
      <TextSection style={{ marginLeft: 'auto', marginRight: 'auto' }}>
        <EditableH1
          onBlur={onBlur}
          zIndex={1}
          contentEditable={contentEditable}
          p={{ _: '20px 25px', md: '10px 0px 10px 0px' }}
          marginTop={{ _: '30px' }}
          lineHeight={{ _: '30px', md: '72px' }}
          textAlign={{ _: 'center' }}
          fontSize={{ _: '18px', md: '36px' }}
          dangerouslySetInnerHTML={{ __html: value }}
          suppressContentEditableWarning={true}
          onInput={onInput}
          onPaste={onPaste}
          onFocus={onFocus}
          onDrop={handleDrop}
          data-id={id}
        />
      </TextSection>
    );
  },
  heading2: ({
    id,
    value,
    contentEditable = false,
    typing = false,
    onBlur,
    onInput,
    onPaste,
    onFocus,
    quotes = false,
    onKeyDown
  }) => {
    if (typing) {
      const { editorMode } = useEditorStore();
      const { addBlocker, removeBlocker } = useSequenceStore();
      const typingBlockerId = `text_typing_${id}`;
      /** Full text for AT: the typewriter starts empty (axe `empty-heading`). */
      const plainText = (value ?? '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const [playAudioEffect, stopAudioEffect] = useAudioEffect(
        audioEffect,
        true
      );

      useEffect(() => {
        try {
          playAudioEffect();
        } catch (error) {
          // Silently ignore audio effect errors
        }
        return () => {
          stopAudioEffect();
        };
      }, []);

      useEffect(() => {
        const shouldBlockNext =
          !contentEditable &&
          !editorMode &&
          getVisibleTextLength(value ?? '') > 0;

        if (shouldBlockNext) {
          addBlocker(typingBlockerId);
        }

        return () => {
          removeBlocker(typingBlockerId);
        };
      }, [
        contentEditable,
        editorMode,
        value,
        typingBlockerId,
        addBlocker,
        removeBlocker
      ]);

      const handleTypingComplete = () => {
        stopAudioEffect();
        removeBlocker(typingBlockerId);
      };

      return (
        <EditableH2
          onBlur={onBlur}
          contentEditable={contentEditable}
          p={{ _: '0px', md: '15px 0px 15px 0px' }}
          lineHeight={{ _: '22px' }}
          fontSize={{ _: '18px' }}
          suppressContentEditableWarning={true}
          onInput={onInput}
          onPaste={onPaste}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onDrop={handleDrop}
          data-id={id}
          aria-label={plainText || undefined}
        >
          {/* Screen readers get the full text via aria-label right away; the
              typewriter animation is visual-only (otherwise the heading is
              empty for AT until typing finishes — axe `empty-heading`). */}
          <span aria-hidden={true}>
            <HtmlTypeAnimation
              html={value ?? ''}
              cursor={true}
              onComplete={handleTypingComplete}
            />
          </span>
        </EditableH2>
      );
    }

    const element = (
      <EditableH2
        onBlur={onBlur}
        contentEditable={contentEditable}
        p={{ _: '0px', md: '15px 0px 15px 0px' }}
        lineHeight={{ _: '20px' }}
        fontSize={{ _: '18px' }}
        dangerouslySetInnerHTML={{ __html: value }}
        suppressContentEditableWarning={true}
        onInput={onInput}
        onPaste={onPaste}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDrop={handleDrop}
        quotes={quotes}
        data-id={id}
      />
    );

    return contentEditable ? <TextSection>{element}</TextSection> : element;
  },
  heading3: ({
    id,
    value,
    contentEditable = false,
    onBlur,
    onInput,
    onPaste,
    onFocus,
    onKeyDown,
    center
  }) => {
    return (
      <EditableH3
        onBlur={onBlur}
        contentEditable={contentEditable}
        {...(center ? { textAlign: { _: 'center' } } : {})}
        dangerouslySetInnerHTML={{ __html: value }}
        suppressContentEditableWarning={true}
        onInput={onInput}
        onPaste={onPaste}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDrop={handleDrop}
        data-id={id}
      />
    );
  },
  heading4: ({
    id,
    value,
    contentEditable = false,
    onBlur,
    onInput,
    onPaste,
    onFocus,
    onKeyDown
  }) => {
    return (
      <EditableH4
        onBlur={onBlur}
        contentEditable={contentEditable}
        dangerouslySetInnerHTML={{ __html: value }}
        suppressContentEditableWarning={true}
        onInput={onInput}
        onPaste={onPaste}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDrop={handleDrop}
        data-id={id}
      />
    );
  },
  heading5: ({
    id,
    value,
    contentEditable = false,
    onBlur,
    onInput,
    onPaste,
    onFocus,
    onKeyDown
  }) => {
    return (
      <EditableH5
        onBlur={onBlur}
        contentEditable={contentEditable}
        dangerouslySetInnerHTML={{ __html: value }}
        suppressContentEditableWarning={true}
        onInput={onInput}
        onPaste={onPaste}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDrop={handleDrop}
        data-id={id}
      />
    );
  },
  sub_heading: ({
    id,
    value,
    contentEditable = false,
    padding,
    icon,
    onBlur,
    onInput,
    onPaste,
    onFocus,
    onKeyDown
  }) => {
    const subtitlePadding = useResponsive(padding);
    // const Icon = getIcon(icon);
    // Kicker / eyebrow label — not a document heading (EL–E30 / CAT-1175).
    // Relative line/letter spacing + no float so WCAG 1.4.12 user styles
    // (EL–E97 / CAT-1246) can expand without clipping.
    // Use a div (not <p>) so Stylus `p { margin-bottom: 2em }` (CAT-1244 /
    // WCAG 1.4.12 paragraph spacing) does not inflate an eyebrow into overlap.
    return (
      <x.div
        color="accentDark"
        className="subtitleStyleForDarkTheme"
        style={{ padding: subtitlePadding }}
        maxW="100%"
        overflow="visible"
      >
        {/* {Icon && <Icon aria-hidden={true} />} */}
        <EditableDiv
          onBlur={onBlur}
          contentEditable={contentEditable}
          fontFamily="'Plus Jakarta Sans', sans-serif"
          fontWeight={600}
          fontSize="14px"
          lineHeight={1.5}
          letterSpacing="0.05em"
          textTransform="uppercase"
          whiteSpace="normal"
          overflow="visible"
          maxW="100%"
          style={{ wordBreak: 'break-word' }}
          m={0}
          dangerouslySetInnerHTML={{ __html: value }}
          suppressContentEditableWarning={true}
          onInput={onInput}
          onPaste={onPaste}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onDrop={handleDrop}
          data-id={id}
        />
      </x.div>
    );
  },
  quote: ({
    id,
    value,
    contentEditable = false,
    onBlur,
    onInput,
    onPaste,
    onFocus,
    onKeyDown
  }) => {
    return (
      <x.div
        position="relative"
        fontSize={{ _: 'lg', lg: 'xl' }}
        fontFamily="title"
      >
        <x.div
          fontSize={{ _: '105px', lg: '70px' }}
          lineHeight={{ _: '80px', lg: '70px' }}
          color="accentDark"
          position="absolute"
          right="100%"
          marginRight={{ _: '-15px', md: '10px' }}
          zIndex={-1}
          top="0"
        >
          “
        </x.div>
        <EditableSpan
          onBlur={onBlur}
          contentEditable={contentEditable}
          dangerouslySetInnerHTML={{ __html: value }}
          suppressContentEditableWarning={true}
          onInput={onInput}
          onPaste={onPaste}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onDrop={handleDrop}
          data-id={id}
        />
      </x.div>
    );
  },
  text: ({
    id,
    value,
    contentEditable = false,
    canScroll,
    className = '',
    onBlur,
    onInput,
    onPaste,
    onFocus,
    padding,
    fontSize,
    maxHeight,
    onKeyDown
  }) => {
    const bodyPadding = useResponsive(padding);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showGradient, setShowGradient] = useState(false);

    const checkScroll = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;

      const hasOverflow = el.scrollHeight > el.clientHeight;
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 5;

      setShowGradient(hasOverflow && !isAtBottom);
    }, []);

    useEffect(() => {
      if (!canScroll) return;

      checkScroll();

      const el = scrollRef.current;
      if (!el) return;

      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(el);

      return () => resizeObserver.disconnect();
    }, [canScroll, checkScroll, value]);

    const handleScroll = useCallback(() => {
      checkScroll();
    }, [checkScroll]);

    const scrollContent = (
      <EditableBodyText
        ref={canScroll ? scrollRef : undefined}
        onBlur={onBlur}
        contentEditable={contentEditable}
        className={className}
        overflowY={canScroll ? 'auto' : undefined}
        onScroll={canScroll ? handleScroll : undefined}
        // FlipCard skips flip when the click is inside this region so wheel /
        // scrollbar keep working (pointer-events must stay enabled).
        {...(canScroll ? { 'data-flip-scroll': true } : {})}
        style={{
          ...(bodyPadding ? { padding: bodyPadding } : {}),
          ...(fontSize ? { fontSize } : {}),
          ...(maxHeight ? { maxHeight } : {}),
          ...(canScroll
            ? {
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin' as const
              }
            : {})
        }}
        dangerouslySetInnerHTML={{ __html: value }}
        suppressContentEditableWarning={true}
        onInput={onInput}
        onPaste={onPaste}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDrop={handleDrop}
        data-id={id}
      />
    );

    if (canScroll) {
      return (
        <TextSection>
          <x.div position="relative">
            {scrollContent}
            {showGradient && <ScrollFadeOverlay aria-hidden />}
          </x.div>
        </TextSection>
      );
    }

    return <TextSection>{scrollContent}</TextSection>;
  },
  huge: ({
    id,
    value,
    contentEditable = false,
    onBlur,
    onInput,
    onPaste,
    onFocus,
    onKeyDown
  }) => {
    return (
      <EditableDiv
        onBlur={onBlur}
        contentEditable={contentEditable}
        fontSize="110px"
        fontFamily="title"
        dangerouslySetInnerHTML={{ __html: value }}
        suppressContentEditableWarning={true}
        onInput={onInput}
        onPaste={onPaste}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDrop={handleDrop}
        data-id={id}
      />
    );
  },
  titleForCard: ({
    id,
    value,
    contentEditable = false,
    onBlur,
    onInput,
    onPaste,
    onFocus,
    onKeyDown
  }) => {
    return (
      <EditableH2
        onBlur={onBlur}
        contentEditable={contentEditable}
        p={{ _: '0px', md: '0px' }}
        lineHeight={{ _: '20px' }}
        fontSize={{ _: '18px' }}
        style={{ marginBottom: '-15px' }}
        dangerouslySetInnerHTML={{ __html: value }}
        suppressContentEditableWarning={true}
        onInput={onInput}
        onPaste={onPaste}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDrop={handleDrop}
        data-id={id}
      />
    );
  }
};

interface Props {
  value: string;
  variant?: TextVariants;
  icon?: IconVariants;
  canScroll?: boolean;
  typing?: boolean;
  className?: string;
  shouldStop?: boolean;
  quotes?: boolean;
  padding?: Responsive<string>;
  fontSize?: string;
  maxHeight?: string;
  lineHeight?: string; // allow overriding line height for certain variants
  center?: boolean; // center alignment for heading1
  editable?: boolean;
  textAlignment?: boolean;
  canUseMarkTag?: boolean;
  disableRewrite?: boolean;
  maxCharacters?: number; // Hard character limit
  allowNewLines?: boolean; // Allow newline characters (default: undefined = no restriction)
  showQuizDropdown?: boolean;
}

export type TextDefinition = ItemDefinition<typeof TEXT, Props>;

export class Text extends Item<typeof TEXT, Props> {
  protected render({
    value,
    variant = 'text',
    icon,
    canScroll = false,
    typing = false,
    className = '',
    id,
    quotes = false,
    padding,
    fontSize,
    maxHeight,
    lineHeight,
    center,
    editable = true,
    textAlignment = true,
    canUseMarkTag = false,
    disableRewrite = false,
    maxCharacters,
    allowNewLines = true,
    showQuizDropdown = false
  }: RenderProps<Props>) {
    const removeNewLines = !allowNewLines;

    // sub_heading, heading1, heading2, and heading3 have server-side 512 char limit
    const effectiveMaxCharacters =
      editable &&
      (variant === 'sub_heading' ||
        variant === 'heading1' ||
        variant === 'heading2' ||
        variant === 'heading3')
        ? 512
        : maxCharacters;

    const { contentEditable, updateSequence } = !editable
      ? {
          contentEditable: false,
          updateSequence: () => {
            /* no-op */
          }
        }
      : useEditorStore();

    // Convert XXX to dropdown elements if showQuizDropdown is enabled
    const [text, setText] = useState<string>(
      showQuizDropdown
        ? convertMarkersToDropdowns(value, contentEditable)
        : value
    );
    const [savedRange, setSavedRange] = useState<Range | null>(null);
    // Update text when value changes (for showQuizDropdown)
    useEffect(() => {
      if (showQuizDropdown) {
        setText(convertMarkersToDropdowns(value, contentEditable));
      } else {
        setText(value);
      }
    }, [value, showQuizDropdown, contentEditable]);

    // Handle drag and drop for quiz dropdown elements
    useEffect(() => {
      if (!showQuizDropdown || !contentEditable) return;

      const container = document.querySelector(`[data-id="${id}"]`);
      if (!container) return;

      let draggedElement: HTMLElement | null = null;

      const handleDragStart = (e: DragEvent) => {
        const target = e.target as HTMLElement;
        if (target.hasAttribute('data-quiz-dropdown')) {
          draggedElement = target;
          e.dataTransfer?.setData('text/plain', 'quiz-dropdown');
          if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
          }
          // Add visual feedback
          target.style.opacity = '0.5';
        }
      };

      const handleDragEnd = (e: DragEvent) => {
        const target = e.target as HTMLElement;
        if (target.hasAttribute('data-quiz-dropdown')) {
          target.style.opacity = '1';
        }
        draggedElement = null;
      };

      const handleDragOver = (e: DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'move';
        }

        if (!draggedElement) return;

        // Get the caret position and set selection there
        const range = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (!range) return;

        // Check if inside the container
        const dropTarget = range.startContainer;
        if (!container.contains(dropTarget)) return;

        // Don't set caret inside the dropdown element itself
        const parentDropdown =
          (dropTarget as Element).closest?.('[data-quiz-dropdown]') ||
          (dropTarget.parentElement as Element)?.closest?.(
            '[data-quiz-dropdown]'
          );
        if (parentDropdown) return;

        // Set the selection/caret at the position - browser will show the blinking cursor
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      };

      const handleDrop = (e: DragEvent) => {
        e.preventDefault();

        if (!draggedElement) return;

        // Get the drop position using caret position
        const range = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (!range) return;

        // Check if dropping inside the same container
        const dropTarget = range.startContainer;
        if (!container.contains(dropTarget)) return;

        // Don't drop inside the dropdown element itself
        const parentDropdown =
          (dropTarget as Element).closest?.('[data-quiz-dropdown]') ||
          (dropTarget.parentElement as Element)?.closest?.(
            '[data-quiz-dropdown]'
          );
        if (parentDropdown) return;

        // Clone the dropdown element
        const newDropdown = draggedElement.cloneNode(true) as HTMLElement;
        newDropdown.style.opacity = '1';

        // Remove the original element
        draggedElement.remove();

        // Insert at the caret position
        range.insertNode(newDropdown);

        // Clean up empty text nodes
        container.normalize();

        // Trigger blur to save the changes
        (container as HTMLElement).blur();
      };

      container.addEventListener('dragstart', handleDragStart as EventListener);
      container.addEventListener('dragend', handleDragEnd as EventListener);
      container.addEventListener('dragover', handleDragOver as EventListener);
      container.addEventListener('drop', handleDrop as EventListener);

      return () => {
        container.removeEventListener(
          'dragstart',
          handleDragStart as EventListener
        );
        container.removeEventListener(
          'dragend',
          handleDragEnd as EventListener
        );
        container.removeEventListener(
          'dragover',
          handleDragOver as EventListener
        );
        container.removeEventListener('drop', handleDrop as EventListener);
      };
    }, [showQuizDropdown, contentEditable, id]);

    const handleSelectionChange = () => {
      const selection = window.getSelection();

      if (selection) {
        const anchorNode = selection.anchorNode;
        const focusNode = selection.focusNode;

        const portalElement =
          anchorNode?.parentElement?.closest('[data-rewrite-portal]') ||
          focusNode?.parentElement?.closest('[data-rewrite-portal]');

        if (portalElement) {
          return;
        }

        const activeElement = document.activeElement;
        if (activeElement && activeElement.closest('[data-rewrite-portal]')) {
          return;
        }

        if (activeElement && activeElement.tagName === 'INPUT') {
          return;
        }
      }

      if (
        selection &&
        selection.rangeCount > 0 &&
        selection.toString().length > 0
      ) {
        try {
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          const selectedElement = range.startContainer.parentElement;

          const currentElement = document.querySelector(`[data-id="${id}"]`);
          if (!currentElement || !currentElement.contains(selectedElement)) {
            return;
          }

          if (selectedElement && selectedElement.isContentEditable) {
            setSavedRange(range);
            activePopupId = id;

            // Set global portal
            setActivePortal({
              id,
              selectedText: selection.toString(),
              savedRange: range,
              popupPosition: calculatePopupPosition(rect, undefined, undefined),
              onRangeChange: setSavedRange,
              variant: showQuizDropdown ? 'sub_heading' : variant,
              textAlignment: textAlignment,
              canUseMarkTag: canUseMarkTag,
              clearRange: () => {
                setSavedRange(null);
                activePopupId = null;
                setActivePortal(null);
              }
            });
          } else {
            setSavedRange(null);
            activePopupId = null;
            setActivePortal(null);
          }
        } catch (error) {
          console.error('Error in handleSelectionChange:', error);
        }
      } else {
        if (!savedRange) {
          activePopupId = null;
          setActivePortal(null);
        }
      }
    };

    useEffect(() => {
      if (contentEditable && !disableRewrite) {
        document.addEventListener('selectionchange', handleSelectionChange);

        return () => {
          document.removeEventListener(
            'selectionchange',
            handleSelectionChange
          );
          if (activePopupId === id && !savedRange) {
            activePopupId = null;
            setActivePortal(null);
          }
        };
      }
    }, [id, contentEditable, disableRewrite]);

    // Additional effect to prevent popup hiding when input gets focus
    useEffect(() => {
      const handleFocusIn = (event: FocusEvent) => {
        const target = event.target as HTMLElement;
        if (target && target.closest('[data-rewrite-portal]')) {
          // Prevent the popup from hiding when any element inside it gets focus
          event.stopPropagation();
        }
      };

      if (contentEditable) {
        document.addEventListener('focusin', handleFocusIn);
        return () => {
          document.removeEventListener('focusin', handleFocusIn);
        };
      }
    }, [contentEditable]);

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      let newText = event.target.innerHTML;
      let defaultText = event.target.innerHTML;

      // Convert dropdown elements back to XXX markers before saving
      if (showQuizDropdown) {
        newText = convertDropdownsToMarkers(newText);
        defaultText = convertDropdownsToMarkers(defaultText);
      }

      const replaceTextAlignStyles = (html: string): string => {
        let processedHtml = html.replace(
          /<(\w+)([^>]*style="[^"]*text-align:\s*(right|center|left|justify)[^"]*"[^>]*)>/gi,
          (
            match: string,
            tagName: string,
            attributes: string,
            alignment: string
          ) => {
            const newAttributes = attributes.replace(
              /style="([^"]*text-align:\s*(right|center|left|justify);?\s*([^"]*))"/gi,
              (
                styleMatch: string,
                beforeTextAlign: string,
                textAlignValue: string,
                afterTextAlign: string
              ) => {
                const newStyle = (beforeTextAlign + afterTextAlign)
                  .replace(/;\s*;/, ';')
                  .replace(/^;\s*/, '')
                  .replace(/;\s*$/, '');

                return newStyle ? `style="${newStyle}"` : '';
              }
            );

            const classAttr = ` class="text-align-${alignment}"`;

            return `<${tagName}${newAttributes}${classAttr}>`;
          }
        );

        processedHtml = processedHtml.replace(/style="[^"]*"/gi, '');

        processedHtml = processedHtml.replace(/\s+/g, ' ');

        processedHtml = processedHtml.replace(
          /(\w+="[^"]*")\s+(\w+="[^"]*")/gi,
          '$1 $2'
        );

        return processedHtml;
      };

      const [part1, part2] = id.split('_');
      const elementId = isNaN(Number(part1)) ? part2 : part1;
      const elementType = isNaN(Number(part1)) ? part1 : part2;

      if (newText.trim() == '') {
        newText = 'Type Here';
        setText(newText);
        defaultText = newText;

        setTimeout(() => {
          event.target.innerHTML = newText;
        }, 0);
      }

      defaultText = replaceTextAlignStyles(defaultText);

      updateSequence(elementId, elementType, defaultText);
    };

    const firstName = useTrainingStore(s => s.firstName);

    const val = useMemo(() => {
      if (text) {
        return text.replace('[[first_name]]', firstName);
      }
    }, [text, firstName]);

    const moveCursorToEnd = (element: HTMLElement) => {
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(element);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    };

    const handleInput = (event: React.ChangeEvent<HTMLDivElement>) => {
      if (contentEditable) {
        const element = event.currentTarget;
        const rawHtml = element.innerHTML;
        // Validate by raw length: &nbsp; = 6 chars, matches server-side
        let decodedText = getDecodedText(rawHtml);
        if (removeNewLines) {
          decodedText = decodedText.replace(/\r?\n|\r/g, '');
        }
        let newText = decodedText;

        if (effectiveMaxCharacters !== undefined || removeNewLines) {
          const rawLength = getRawContentLength(rawHtml, removeNewLines);
          const needsSanitize =
            rawLength > (effectiveMaxCharacters ?? Infinity) || removeNewLines;

          if (needsSanitize) {
            const sanitized = sanitizeRawHtmlContent(rawHtml, {
              ...(effectiveMaxCharacters !== undefined && {
                maxCharacters: effectiveMaxCharacters
              }),
              removeNewLines
            });

            if (sanitized !== decodedText) {
              element.textContent = sanitized;
              newText = sanitized;
              setText(sanitized);
            }
          }
        }

        const textWithoutHtml = stripHtmlTags(newText);

        if (textWithoutHtml?.trim() == '') {
          newText = ' ';
          event.currentTarget.textContent = newText;
          setText(newText);
          moveCursorToEnd(event.currentTarget);
        }
      }
    };

    // Helper function to check if selection intersects with dropdown
    const selectionIntersectsDropdown = (container: HTMLElement): boolean => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return false;

      const range = selection.getRangeAt(0);
      if (range.collapsed) return false;

      const dropdown = container.querySelector('[data-quiz-dropdown]');
      if (!dropdown) return false;

      const dropdownRange = document.createRange();
      dropdownRange.selectNode(dropdown);

      // Check if ranges intersect
      const rangeStart = range.compareBoundaryPoints(
        Range.START_TO_START,
        dropdownRange
      );
      const rangeEnd = range.compareBoundaryPoints(
        Range.END_TO_END,
        dropdownRange
      );

      // Selection includes dropdown if it spans across it
      if (rangeStart <= 0 && rangeEnd >= 0) return true;
      if (rangeStart >= 0 && rangeEnd <= 0) return true;

      return false;
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (removeNewLines && event.key === 'Enter') {
        event.preventDefault();
        return;
      }

      if (
        effectiveMaxCharacters !== undefined &&
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        const element = event.currentTarget;
        const currentRawLength = getRawContentLength(
          element.innerHTML,
          removeNewLines
        );
        const selection = window.getSelection();
        const hasSelection = selection && !selection.isCollapsed;

        if (!hasSelection && currentRawLength >= effectiveMaxCharacters) {
          event.preventDefault();
          return;
        }
      }

      // Prevent replacing dropdown with any character when selected
      if (showQuizDropdown && !event.ctrlKey && !event.metaKey) {
        // Check if it's a printable character (length 1) or special keys that would replace selection
        const isPrintableKey = event.key.length === 1;
        const isReplacingKey =
          isPrintableKey || event.key === 'Enter' || event.key === 'Tab';

        if (isReplacingKey) {
          const selection = window.getSelection();
          if (selection && !selection.isCollapsed) {
            if (selectionIntersectsDropdown(event.currentTarget)) {
              event.preventDefault();
              return;
            }
          }
        }
      }

      // Prevent deleting quiz dropdown elements with Backspace/Delete
      if (
        showQuizDropdown &&
        (event.key === 'Backspace' || event.key === 'Delete')
      ) {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);

        // Only check if cursor is directly adjacent to a dropdown
        if (range.collapsed) {
          const container = range.startContainer;

          // Get the character before/after cursor position
          if (event.key === 'Backspace' && range.startOffset > 0) {
            // Check if previous character is inside or is the dropdown
            const prevChar = container.textContent?.charAt(
              range.startOffset - 1
            );
            // If it's a zero-width space, check what's before it
            if (prevChar === '\u200B') {
              // Check if there's a dropdown right before the zero-width space
              const prevSibling = container.previousSibling as HTMLElement;
              if (prevSibling?.hasAttribute?.('data-quiz-dropdown')) {
                // Skip the zero-width space, move cursor before dropdown
                event.preventDefault();
                const newRange = document.createRange();
                newRange.setStartBefore(prevSibling);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
                return;
              }
            }
          }

          if (event.key === 'Delete') {
            const textLength = container.textContent?.length || 0;
            if (range.startOffset < textLength) {
              const nextChar = container.textContent?.charAt(range.startOffset);
              // If it's a zero-width space, check what's after it
              if (nextChar === '\u200B') {
                const nextSibling = container.nextSibling as HTMLElement;
                if (nextSibling?.hasAttribute?.('data-quiz-dropdown')) {
                  // Skip the zero-width space, move cursor after dropdown
                  event.preventDefault();
                  const newRange = document.createRange();
                  newRange.setStartAfter(nextSibling);
                  newRange.collapse(true);
                  selection.removeAllRanges();
                  selection.addRange(newRange);
                  return;
                }
              }
            }
          }
        }

        // Check if selection includes the dropdown
        if (!range.collapsed) {
          if (selectionIntersectsDropdown(event.currentTarget)) {
            event.preventDefault();
            return;
          }
        }
      }
    };

    const handlePasteEvent = (e: React.ClipboardEvent<HTMLDivElement>) => {
      const hasValidation =
        effectiveMaxCharacters !== undefined || removeNewLines;

      if (!hasValidation) {
        handlePaste(e);
        return;
      }

      e.preventDefault();
      const rawPastedText = e.clipboardData.getData('text');
      let pastedText = getDecodedText(rawPastedText);
      if (removeNewLines) {
        pastedText = pastedText.replace(/\r?\n|\r/g, '');
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      const element = e.currentTarget;

      // Use raw length for limit: &nbsp; = 6 chars, matches server
      let maxInsertLength = pastedText.length;
      if (effectiveMaxCharacters !== undefined) {
        const fullRawLength = getRawContentLength(
          element.innerHTML,
          removeNewLines
        );
        const fragment = range.cloneContents();
        const div = document.createElement('div');
        div.appendChild(fragment);
        const selectedRawLength = getRawContentLength(
          div.innerHTML,
          removeNewLines
        );
        const remainingRawLength = fullRawLength - selectedRawLength;
        maxInsertLength = Math.max(
          0,
          effectiveMaxCharacters - remainingRawLength
        );
      }

      const finalSanitized = pastedText.slice(0, maxInsertLength);

      range.deleteContents();

      const textNode = document.createTextNode(finalSanitized);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    };

    const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
      if (
        contentEditable &&
        event.currentTarget.textContent?.toLowerCase() === 'type here'
      ) {
        const element = event.currentTarget;
        element.textContent = '';
        element.focus();

        setTimeout(() => {
          moveCursorToEnd(element);
        }, 0);
      }

      if (contentEditable) {
        const convertClassesToStyles = (element: HTMLElement) => {
          const elements = element.querySelectorAll('[class*="text-align-"]');
          elements.forEach(el => {
            const classList = el.classList;
            let alignment = '';

            if (classList.contains('text-align-left')) {
              alignment = 'left';
            } else if (classList.contains('text-align-center')) {
              alignment = 'center';
            } else if (classList.contains('text-align-right')) {
              alignment = 'right';
            } else if (classList.contains('text-align-justify')) {
              alignment = 'justify';
            }

            if (alignment) {
              (el as HTMLElement).style.textAlign = alignment;
              classList.remove(`text-align-${alignment}`);
            }
          });

          const emptyClasses = element.querySelectorAll('[class=""]');
          emptyClasses.forEach(el => {
            (el as HTMLElement).removeAttribute('class');
          });
        };

        convertClassesToStyles(event.currentTarget);
      }
    };

    const Comp = variantTable[variant];

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't hide popup if clicking inside the popup or its children
      if (target.closest('[data-rewrite-portal]')) {
        return;
      }

      // Don't hide popup if clicking on interactive elements
      if (
        target.closest('button') ||
        target.closest('input') ||
        target.closest('a') ||
        target.closest('svg') ||
        target.closest('.link-input') ||
        target.closest('.link-url') ||
        target.closest('.Tooltip') ||
        target.closest('[data-rewrite-portal]')
      ) {
        return;
      }

      // Additional check for active element
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.closest('[data-rewrite-portal]') ||
          activeElement.closest('.link-input') ||
          activeElement.closest('input'))
      ) {
        return;
      }

      // Hide popup only if clicking outside
      setSavedRange(null);
      activePopupId = null;
      setActivePortal(null);
    };

    useEffect(() => {
      if (contentEditable) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
    }, [contentEditable]);

    // Learner mode: an empty heading is announced as a heading with no name
    // (axe `empty-heading`), so don't render it at all. In the editor the
    // element must stay so the author can click into it and type.
    if (
      !contentEditable &&
      HEADING_VARIANTS.includes(variant) &&
      !hasVisibleText(val || text)
    ) {
      return null;
    }

    if (val || text) {
      return (
        <>
          <Comp
            id={id}
            value={contentEditable ? text : val || text}
            contentEditable={contentEditable}
            onBlur={contentEditable ? handleBlur : undefined}
            icon={icon}
            canScroll={canScroll}
            typing={typing}
            className={className}
            onInput={contentEditable ? handleInput : undefined}
            onPaste={contentEditable ? handlePasteEvent : undefined}
            onKeyDown={contentEditable ? handleKeyDown : undefined}
            onFocus={contentEditable ? handleFocus : undefined}
            quotes={quotes}
            padding={padding}
            fontSize={fontSize}
            maxHeight={maxHeight}
            lineHeight={lineHeight}
            center={center}
            textAlignment={textAlignment}
            canUseMarkTag={canUseMarkTag}
          />
        </>
      );
    }
    return null;
  }
}

registry[TEXT] = Text;
